import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, PlusCircle, Trash2, Lock, Unlock, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("ae_token")}` });

const Pill = ({ children, color = "zinc" }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold px-2 py-0.5 border bg-${color}-50 border-${color}-200 text-${color}-800`}>{children}</span>
);

/**
 * BandCalibrationPanel — admin UI for the UK undergraduate band calibration
 * layer. Defaults to disabled; the "Enable" switch is gated behind a server
 * check (min_anchors must be met with non-validation anchors). Validation
 * anchors are stored but excluded from the computed offset.
 */
export default function BandCalibrationPanel() {
  const [status, setStatus] = useState(null);
  const [anchors, setAnchors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    filename: "", official_grade: "", predicted_grade: "", assessment_type: "",
    institution: "", notes: "", is_validation: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        axios.get(`${API}/admin/band-calibration/status`, { headers: authHeaders() }),
        axios.get(`${API}/admin/band-calibration/anchors`, { headers: authHeaders() }),
      ]);
      setStatus(s.data);
      setAnchors(a.data.anchors || []);
    } catch (e) {
      toast.error("Failed to load band calibration status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addAnchor = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        official_grade: parseFloat(form.official_grade),
        predicted_grade: parseFloat(form.predicted_grade),
      };
      await axios.post(`${API}/admin/band-calibration/anchors`, payload, { headers: authHeaders() });
      toast.success(form.is_validation ? "Validation anchor added" : "Calibration anchor added");
      setForm({ filename: "", official_grade: "", predicted_grade: "", assessment_type: "", institution: "", notes: "", is_validation: true });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add anchor");
    } finally {
      setSaving(false);
    }
  };

  const removeAnchor = async (id) => {
    if (!window.confirm("Remove this anchor?")) return;
    try {
      await axios.delete(`${API}/admin/band-calibration/anchors/${id}`, { headers: authHeaders() });
      toast.success("Anchor removed");
      await load();
    } catch (e) { toast.error("Failed to remove anchor"); }
  };

  const toggleEnabled = async (next) => {
    try {
      await axios.post(`${API}/admin/band-calibration/settings`, { enabled: next }, { headers: authHeaders() });
      toast.success(next ? "Band calibration enabled" : "Band calibration disabled");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update settings");
    }
  };

  const updateMinAnchors = async (n) => {
    try {
      await axios.post(`${API}/admin/band-calibration/settings`, { min_anchors: parseInt(n, 10) }, { headers: authHeaders() });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update min_anchors"); }
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground py-10"><Loader2 className="animate-spin" size={18} /> Loading band calibration…</div>;
  if (!status) return null;

  const s = status.settings;
  const a = status.aggregate;
  const nonValidationCount = anchors.filter((x) => !x.is_validation).length;
  const canEnable = nonValidationCount >= s.min_anchors;

  return (
    <div data-testid="band-calibration-panel" className="space-y-6">
      {/* Status banner */}
      <div className={`border p-6 ${s.enabled ? "border-[#15803D] bg-[#15803D]/5" : "border-border bg-secondary"}`}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {s.enabled ? <Unlock size={16} className="text-[#15803D]" /> : <Lock size={16} className="text-muted-foreground" />}
              <p className="overline text-oxford">UK Undergraduate Band Calibration</p>
              <Pill color={s.enabled ? "emerald" : "zinc"}>{s.enabled ? "LIVE" : "DORMANT"}</Pill>
              <Pill color="indigo">{s.version}</Pill>
            </div>
            <p className="text-sm text-muted-foreground max-w-2xl">
              Aligns the <strong>final</strong> predicted grade with the UK undergraduate 40/50/60/70 bands using validated real anchor papers.
              Criterion scores are never touched. Validation anchors (used as a tolerance check) are <strong>excluded</strong> from the computed offset.
              The layer only applies when the non-validation pool reaches <strong>{s.min_anchors}</strong> papers.
            </p>
          </div>
          <button
            type="button"
            onClick={() => toggleEnabled(!s.enabled)}
            disabled={!s.enabled && !canEnable}
            className={`px-4 py-2 text-sm font-medium border ${s.enabled ? "border-[#B91C1C] text-[#B91C1C]" : canEnable ? "border-oxford text-oxford" : "border-border text-muted-foreground cursor-not-allowed"}`}
            data-testid="band-cal-toggle"
          >
            {s.enabled ? "Disable" : canEnable ? "Enable" : `Need ${s.min_anchors - nonValidationCount} more non-validation anchors`}
          </button>
        </div>
      </div>

      {/* Aggregate stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
        <Metric label="Non-validation anchors" value={a.sample_size} sub={`min required: ${s.min_anchors}`} />
        <Metric label="Validation anchors" value={status.validation_anchor_count} sub="regression-only" />
        <Metric label="Proposed offset" value={`${a.raw_offset > 0 ? "+" : ""}${a.raw_offset}pp`} sub={`${a.method} · clamp ±${s.max_offset_pp}pp`} />
        <Metric label={s.enabled ? "Applied offset" : "Applied (dormant)"} value={`${a.offset > 0 ? "+" : ""}${a.offset}pp`} sub={`MAE ${a.mae} · bias ${a.bias}`} />
      </div>

      {/* Add anchor form */}
      <div className="border border-border p-5" data-testid="band-cal-add-form">
        <p className="overline text-muted-foreground mb-3 flex items-center gap-1.5"><PlusCircle size={14} /> Add anchor paper</p>
        <form onSubmit={addAnchor} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Input label="Filename" value={form.filename} onChange={(v) => setForm({ ...form, filename: v })} placeholder="e.g. module4.docx" />
          <Input label="Official grade (%)" value={form.official_grade} onChange={(v) => setForm({ ...form, official_grade: v })} required />
          <Input label="MyAE predicted grade (%)" value={form.predicted_grade} onChange={(v) => setForm({ ...form, predicted_grade: v })} required />
          <Input label="Assessment type" value={form.assessment_type} onChange={(v) => setForm({ ...form, assessment_type: v })} placeholder="e.g. Reflection" />
          <Input label="Institution" value={form.institution} onChange={(v) => setForm({ ...form, institution: v })} placeholder="University name" />
          <Input label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
          <label className="flex items-center gap-2 text-sm text-foreground mt-6 lg:col-span-2">
            <input type="checkbox" checked={form.is_validation} onChange={(e) => setForm({ ...form, is_validation: e.target.checked })} data-testid="band-cal-is-validation" />
            <Shield size={14} className="text-oxford" />
            <span>Validation anchor only (regression test — excluded from offset)</span>
          </label>
          <div className="lg:col-span-4 flex items-center gap-3">
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium bg-oxford text-white hover:bg-[#2C5282] disabled:opacity-50" data-testid="band-cal-add-submit">
              {saving ? "Adding…" : "Add anchor"}
            </button>
            <label className="text-xs text-muted-foreground">
              min_anchors:
              <input type="number" min="1" value={s.min_anchors}
                     onBlur={(e) => updateMinAnchors(e.target.value)}
                     defaultValue={s.min_anchors}
                     className="ml-1 w-16 px-1 py-0.5 border border-border text-xs"
                     data-testid="band-cal-min-anchors" />
            </label>
          </div>
        </form>
      </div>

      {/* Anchor table */}
      <div className="border border-border" data-testid="band-cal-table">
        <table className="w-full text-[13px]">
          <thead className="bg-secondary border-b border-border">
            <tr className="text-left">
              <Th>Filename</Th><Th>Official</Th><Th>Predicted</Th><Th>Δ</Th>
              <Th>Class</Th><Th>Role</Th><Th>Engine</Th><Th>Prompt</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {anchors.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">No anchors yet. Add validated papers above.</td></tr>}
            {anchors.map((x) => (
              <tr key={x.id} className="border-b border-border last:border-0" data-testid={`band-cal-row-${x.id}`}>
                <Td mono>{x.filename}</Td>
                <Td>{x.official_grade}%</Td>
                <Td>{x.predicted_grade}%</Td>
                <Td><span className={x.delta > 0 ? "text-emerald-700" : x.delta < 0 ? "text-rose-700" : ""}>{x.delta > 0 ? "+" : ""}{x.delta}pp</span></Td>
                <Td className="text-xs">{x.official_classification}</Td>
                <Td>{x.is_validation ? <Pill color="indigo">VALIDATION</Pill> : <Pill color="emerald">CALIBRATION</Pill>}</Td>
                <Td className="font-mono text-[11px]">{x.engine_version}</Td>
                <Td className="font-mono text-[11px]">{x.prompt_version}</Td>
                <Td>
                  <button type="button" onClick={() => removeAnchor(x.id)} className="text-rose-700 hover:text-rose-900" data-testid={`band-cal-delete-${x.id}`}>
                    <Trash2 size={14} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Metric = ({ label, value, sub }) => (
  <div className="bg-card p-4">
    <p className="overline text-muted-foreground">{label}</p>
    <p className="font-serif text-2xl text-foreground mt-1 tabular-nums">{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground mt-1">{sub}</p>}
  </div>
);

const Input = ({ label, value, onChange, placeholder, required }) => (
  <label className="text-xs text-muted-foreground">
    <span>{label}</span>
    <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required}
           className="mt-1 w-full px-2 py-1.5 border border-border text-sm text-foreground bg-card" />
  </label>
);

const Th = ({ children }) => <th className="px-3 py-2 overline text-muted-foreground font-normal">{children}</th>;
const Td = ({ children, mono, className = "" }) => (
  <td className={`px-3 py-2 ${mono ? "font-mono text-[12px]" : ""} ${className}`}>{children}</td>
);
